import { getAllFieldSets } from "@/api/field-definitions/field-definitions";
import {
    IFieldDefinitionWithProperties,
    IFieldPropertyWithDatatype,
    IFieldSet,
} from "@/api/field-definitions/field-definitions.types";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import {
    Alert,
    Box,
    Chip,
    Collapse,
    IconButton,
    Stack,
    styled,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

const FieldSetHeader = styled(Box)<{ isExpanded?: boolean }>(
    ({ isExpanded = false, theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
        padding: "12px 16px",
        paddingRight: "12px",
        marginBottom: isExpanded ? 0 : "8px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
    })
);

const FieldSetContent = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    marginBottom: "8px",
    marginTop: 0,
    overflow: "hidden",
}));

const FieldDefinitionHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    padding: "8px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
}));

export const FieldsAccordionView = () => {
    const [expandedFieldSetId, setExpandedFieldSetId] = useState<number | null>(
        null
    );

    const { data, isFetching, error } = useQuery({
        queryKey: ["admin-all-fieldsets"],
        queryFn: () => getAllFieldSets(),
    });

    const fieldsets = data?.fieldsets ?? [];

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    Failed to load field definitions. Please try again.
                </Alert>
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {isFetching && fieldsets.length === 0 && <LoadingIndicator />}

            {!isFetching && fieldsets.length === 0 && (
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No fieldsets available.
                    </Typography>
                </Box>
            )}

            {fieldsets.length > 0 && (
                <Stack>
                    {fieldsets.map((fieldset) => (
                        <FieldSetAccordion
                            key={fieldset.id}
                            fieldset={fieldset}
                            expanded={expandedFieldSetId === fieldset.id}
                            onToggle={() =>
                                setExpandedFieldSetId(
                                    expandedFieldSetId === fieldset.id
                                        ? null
                                        : fieldset.id
                                )
                            }
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

interface FieldSetAccordionProps {
    fieldset: IFieldSet;
    expanded: boolean;
    onToggle: () => void;
}

const FieldSetAccordion = ({
    fieldset,
    expanded,
    onToggle,
}: FieldSetAccordionProps) => {
    return (
        <Box sx={{ width: "100%" }}>
            <FieldSetHeader isExpanded={expanded} onClick={onToggle}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flex: 1 }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        {fieldset.policy_type.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        ({fieldset.policy_type.short_name})
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        - {fieldset.field_definitions.length} fields
                    </Typography>
                </Stack>
                <IconButton
                    size="small"
                    sx={{
                        color: "inherit",
                        padding: "4px",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                    }}
                >
                    {expanded ? <ExpandMore /> : <ChevronRight />}
                </IconButton>
            </FieldSetHeader>

            <Collapse in={expanded}>
                <FieldSetContent>
                    {fieldset.field_definitions.length === 0 ? (
                        <Box p={2}>
                            <Typography variant="body2" color="text.secondary">
                                No field definitions for this policy type.
                            </Typography>
                        </Box>
                    ) : (
                        fieldset.field_definitions.map((fieldDef) => (
                            <FieldDefinitionRow
                                key={fieldDef.id}
                                fieldDef={fieldDef}
                            />
                        ))
                    )}
                </FieldSetContent>
            </Collapse>
        </Box>
    );
};

interface FieldDefinitionRowProps {
    fieldDef: IFieldDefinitionWithProperties;
}

const FieldDefinitionRow = ({ fieldDef }: FieldDefinitionRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasProperties = fieldDef.properties.length > 0;

    return (
        <Box>
            <FieldDefinitionHeader
                onClick={() => hasProperties && setIsExpanded(!isExpanded)}
                sx={{ cursor: hasProperties ? "pointer" : "default" }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                        {fieldDef.name}
                    </Typography>
                    {fieldDef.is_array && (
                        <Chip
                            label="List"
                            size="small"
                            color="primary"
                            sx={{ fontSize: "0.65rem", height: 20 }}
                        />
                    )}
                    {hasProperties && (
                        <Typography variant="caption" color="text.secondary">
                            ({fieldDef.properties.length} properties)
                        </Typography>
                    )}
                </Stack>
                {hasProperties && (
                    <IconButton size="small" sx={{ p: 0.5 }}>
                        {isExpanded ? (
                            <ExpandMore fontSize="small" />
                        ) : (
                            <ChevronRight fontSize="small" />
                        )}
                    </IconButton>
                )}
            </FieldDefinitionHeader>

            {hasProperties && (
                <Collapse in={isExpanded}>
                    <Stack>
                        {fieldDef.properties.map((prop) => (
                            <FieldPropertyRow key={prop.id} property={prop} />
                        ))}
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
};

interface FieldPropertyRowProps {
    property: IFieldPropertyWithDatatype;
}

const FieldPropertyRow = ({ property }: FieldPropertyRowProps) => {
    return (
        <Box
            sx={{
                px: 2,
                py: 0.75,
                pl: 5,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                "&:last-child": { borderBottom: 0 },
                "&:hover": { bgcolor: "action.hover" },
            }}
        >
            <Typography variant="body2">{property.name}</Typography>
            <Chip
                label={property.datatype}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem", height: 22 }}
            />
        </Box>
    );
};
